// TL-5 hit-testing: 6 px screen tolerance on strokes, fills hit inside when fill ≠ none,
// topmost wins, window/crossing marquee semantics.
import { dist, angleOf, sub, type Bounds, type Vec } from "../core/geometry";
import {
  arcContainsAngle,
  circleRectIntersects,
  distPointSeg,
  pointInPolygon,
  pointInRect,
  rectContainsRect,
  rectsOverlap,
  segRectIntersects
} from "../core/intersect";
import type { Doc, Entity } from "../doc/entities";
import { dimensionGeometry, dimensionText, labelTextBox } from "../doc/derived";
import { entityBounds } from "./bounds";
import { BASE_PPMM } from "./camera";
import { pointInImage } from "./imageGeom";
import type { SpatialIndex } from "./spatialIndex";

export const HIT_TOL_PX = 6;

function segments(pts: Vec[], closed: boolean): [Vec, Vec][] {
  const out: [Vec, Vec][] = [];
  for (let i = 0; i < pts.length - 1; i++) out.push([pts[i]!, pts[i + 1]!]);
  if (closed && pts.length > 2) out.push([pts[pts.length - 1]!, pts[0]!]);
  return out;
}

/** Precise point-vs-entity test (world units, world tolerance). */
export function hitEntity(entity: Entity, doc: Doc, p: Vec, tol: number): boolean {
  switch (entity.type) {
    case "marker": {
      if (entity.kind === "cloud" && entity.points.length >= 2) {
        // hit on the bounding rectangle perimeter (scallops hug the edges)
        const a = entity.points[0]!;
        const b = entity.points[1]!;
        const rect = [
          { x: a.x, y: a.y },
          { x: b.x, y: a.y },
          { x: b.x, y: b.y },
          { x: a.x, y: b.y }
        ];
        return segments(rect, true).some(([p0, p1]) => distPointSeg(p, p0, p1) <= tol);
      }
      return segments(entity.points, false).some(([a, b]) => distPointSeg(p, a, b) <= tol);
    }
    case "polyline": {
      for (const [a, b] of segments(entity.points, entity.closed)) {
        if (distPointSeg(p, a, b) <= tol) return true;
      }
      if (entity.closed && entity.style.fill.mode !== "none") {
        return pointInPolygon(p, entity.points);
      }
      return false;
    }
    case "circle": {
      const d = dist(p, entity.center);
      if (Math.abs(d - entity.r) <= tol) return true;
      return entity.style.fill.mode !== "none" && d <= entity.r;
    }
    case "arc": {
      const d = dist(p, entity.center);
      if (Math.abs(d - entity.r) > tol) return false;
      return arcContainsAngle(entity.startDeg, entity.sweepDeg, angleOf(sub(p, entity.center)));
    }
    case "image":
      return pointInImage(entity, p);
    case "label": {
      // only the text selects a label; its bounds (head + leader + text) covered the parts under the leader
      const t = labelTextBox(entity);
      return p.x >= t.minX - tol && p.x <= t.maxX + tol && p.y >= t.minY - tol && p.y <= t.maxY + tol;
    }
    case "dimension":
      return hitDimension(entity, doc, p, tol);
    case "group": {
      for (const childId of entity.children) {
        const child = doc.entities[childId];
        if (child && !child.hidden && hitEntity(child, doc, p, tol)) return true;
      }
      return false;
    }
    default:
      // text/image/grid: AABB hit until their milestones add precise tests
      return pointInRect(p, entityBounds(entity, doc));
  }
}

/**
 * A dimension selects on its dimension line and on its value text only. Its bounds
 * span the measured part (a dimension across a pool covered the whole pool), and the
 * extension lines run down onto the part, so neither selects it.
 * The text is paper-sized (constant on screen): its size comes from the camera scale,
 * which the tolerance carries (tol = HIT_TOL_PX / scale).
 */
function hitDimension(entity: Extract<Entity, { type: "dimension" }>, doc: Doc, p: Vec, tol: number): boolean {
  const g = dimensionGeometry(doc, entity);
  if (!g) return false;
  if (distPointSeg(p, g.pa, g.pb) <= tol) return true;
  const pm = (BASE_PPMM * tol) / HIT_TOL_PX;
  const size = doc.dimStyle.textStyle.size * pm;
  const halfW = (dimensionText(doc, entity, g.value).length * size * 0.6) / 2;
  const rad = (-(entity.kind === "linear" ? g.textAngleDeg : 0) * Math.PI) / 180;
  const dx = p.x - g.textPos.x;
  const dy = p.y - g.textPos.y;
  const lx = dx * Math.cos(rad) - dy * Math.sin(rad);
  const ly = dx * Math.sin(rad) + dy * Math.cos(rad);
  // the text sits on the line (baseline a little above it), centred on textPos
  return Math.abs(lx) <= halfW + tol && ly <= tol && ly >= -(size * 1.25 + 0.5 * pm) - tol;
}

export interface HitContext {
  doc: Doc;
  index: SpatialIndex;
  /** Group edit-in-place: hits on THIS group's children return the child id. */
  insideGroup?: string;
}

/** Topmost hit at p, or null. Never returns group children directly — resolves to the
 *  group (except the `insideGroup` being edited, whose children hit individually). */
export function hitTest(ctx: HitContext, p: Vec, tol: number): string | null {
  const { doc, index } = ctx;
  const near = new Set(
    index.tree.query({ minX: p.x - tol, minY: p.y - tol, maxX: p.x + tol, maxY: p.y + tol })
  );
  const parentGroup = groupParentMap(doc);
  // Iterate top layer → bottom, top z → bottom.
  for (let li = doc.layers.length - 1; li >= 0; li--) {
    const layer = doc.layers[li]!;
    if (!layer.visible || layer.locked) continue;
    const order = doc.zOrder[layer.id] ?? [];
    for (let zi = order.length - 1; zi >= 0; zi--) {
      const id = order[zi]!;
      const entity = doc.entities[id];
      if (!entity || entity.hidden) continue;
      const memberHit =
        entity.type === "group"
          ? entity.children.some((c) => near.has(c)) || near.has(id)
          : near.has(id);
      if (!memberHit) continue;
      if (hitEntity(entity, doc, p, tol)) {
        if (entity.type === "group" && entity.id === ctx.insideGroup) {
          // editing this group: resolve to the topmost child under the cursor
          for (let ci = entity.children.length - 1; ci >= 0; ci--) {
            const child = doc.entities[entity.children[ci]!];
            if (child && !child.hidden && hitEntity(child, doc, p, tol)) return child.id;
          }
          continue;
        }
        return parentGroup.get(id) ?? id;
      }
    }
  }
  return null;
}

function groupParentMap(doc: Doc): Map<string, string> {
  const map = new Map<string, string>();
  for (const e of Object.values(doc.entities)) {
    if (e.type === "group") for (const c of e.children) map.set(c, e.id);
  }
  return map;
}

/** Fine geometry-vs-rect test for crossing marquee. */
function entityTouchesRect(entity: Entity, doc: Doc, r: Bounds): boolean {
  switch (entity.type) {
    case "polyline":
    case "marker": {
      const closed = entity.type === "polyline" && entity.closed;
      if (entity.points.some((p) => pointInRect(p, r))) return true;
      if (segments(entity.points, closed).some(([a, b]) => segRectIntersects(a, b, r))) return true;
      // rect entirely inside a filled polygon counts as touching
      return closed && pointInPolygon({ x: r.minX, y: r.minY }, entity.points);
    }
    case "circle":
      return circleRectIntersects(entity.center, entity.r, r);
    case "arc": {
      // sample-based (spec 05: acceptable approximation for arc-vs-rect)
      for (let i = 0; i <= 16; i++) {
        const deg = entity.startDeg + (entity.sweepDeg * i) / 16;
        const rad = (deg * Math.PI) / 180;
        const p = { x: entity.center.x + entity.r * Math.cos(rad), y: entity.center.y + entity.r * Math.sin(rad) };
        if (pointInRect(p, r)) return true;
      }
      return false;
    }
    case "group":
      return entity.children.some((c) => {
        const child = doc.entities[c];
        return child && !child.hidden && entityTouchesRect(child, doc, r);
      });
    case "label":
      return rectsOverlap(labelTextBox(entity), r) || segRectIntersects(entity.head, entity.tail, r);
    case "dimension": {
      const g = dimensionGeometry(doc, entity);
      return !!g && (segRectIntersects(g.pa, g.pb, r) || pointInRect(g.textPos, r));
    }
    default:
      return rectsOverlap(entityBounds(entity, doc), r);
  }
}

/** TL-5 marquee: window = fully contained; crossing = touching. Groups resolve to the group id. */
export function marqueeTest(ctx: HitContext, rect: Bounds, mode: "window" | "crossing"): string[] {
  const { doc, index } = ctx;
  const near = new Set(index.tree.query(rect));
  const parentGroup = groupParentMap(doc);
  const out = new Set<string>();
  for (const layer of doc.layers) {
    if (!layer.visible || layer.locked) continue;
    for (const id of doc.zOrder[layer.id] ?? []) {
      const entity = doc.entities[id];
      if (!entity || entity.hidden || parentGroup.has(id)) continue;
      const candidate =
        entity.type === "group" ? entity.children.some((c) => near.has(c)) : near.has(id);
      if (!candidate) continue;
      const b = entityBounds(entity, doc);
      if (mode === "window") {
        if (rectContainsRect(rect, b)) out.add(id);
      } else if (rectsOverlap(rect, b) && entityTouchesRect(entity, doc, rect)) {
        out.add(id);
      }
    }
  }
  return [...out];
}
